import type { CSSProperties } from 'react';

/**
 * The Amassa mark (logo v3, 2026-10-01): a sienna rounded square, the knot
 * and the ring around the dot cut out (see-through), a cobalt dot.
 *
 * Two shapes:
 *   · variant="chip"  the logo itself, drawn from `web/public/amassa-logo-v3.png`
 *                     (its colours are the brand's and do not change with
 *                     the theme, so the file is the truest render; the knot
 *                     shows whatever is behind it, as in the logo).
 *   · variant="mark"  just the knot, as a tintable alpha mask
 *                     (`web/public/amassa-knot-mask-v3.png`, cut from the
 *                     logo: knot, ring and dot are ink) filled by `fill`,
 *                     with the dot in `--amassa-dot`. For watermarks,
 *                     heroes, loaders.
 *
 * FLAT, deliberately: no gradient and no inset highlight on the chip (the
 * logo has neither). An optional outer glow sits behind it.
 */

const LOGO_URL = '/amassa-logo-v3.png';
const KNOT_URL = '/amassa-knot-mask-v3.png';

interface Props {
  /** Pixel size of the square. */
  size?: number;
  variant?: 'chip' | 'mark';
  /**
   * The knot fill for variant="mark" (any CSS background: color or
   * gradient). Default is the mark ink. Ignored for "chip" (the logo).
   */
  fill?: string;
  /** Add the resting brand glow behind a chip. */
  glow?: boolean;
  style?: CSSProperties;
  className?: string;
  title?: string;
}

function knotMask(size: number, inset = 0): CSSProperties {
  const s = `${size - inset * 2}px`;
  return {
    width: s,
    height: s,
    WebkitMaskImage: `url(${KNOT_URL})`,
    maskImage: `url(${KNOT_URL})`,
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
  };
}

export function AmassaMark({
  size = 28,
  variant = 'chip',
  fill = 'var(--amassa-mark, var(--grad-signal))',
  glow = false,
  style,
  className,
  title = 'Amassa',
}: Props) {
  if (variant === 'mark') {
    return (
      <span
        role="img"
        aria-label={title}
        className={className}
        style={{
          ...knotMask(size),
          position: 'relative',
          display: 'inline-block',
          background: fill,
          flexShrink: 0,
          ...style,
        }}
      >
        <KnotDot />
      </span>
    );
  }

  // chip — the logo
  return (
    <img
      src={LOGO_URL}
      alt={title}
      width={size}
      height={size}
      className={className}
      draggable={false}
      style={{
        display: 'inline-block',
        flexShrink: 0,
        // Outer halo only, and only when asked for: ambient light behind the
        // mark, following its shape (a box-shadow would square it off).
        filter: glow ? 'drop-shadow(0 6px 14px color-mix(in oklab, var(--brand) 55%, transparent))' : undefined,
        ...style,
      }}
    />
  );
}

/**
 * The dot at the heart of the knot, in its own ink (`--amassa-dot`, cobalt in
 * the Quotinc-family interface; unset, it stays the knot's fill). It lives
 * INSIDE the masked knot span, sized and placed exactly on the logo's dot
 * (centre 49.3% / 52.3%, 16% across), round, so the ring around it keeps the
 * knot's fill.
 */
function KnotDot() {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        left: '49.3%',
        top: '52.3%',
        width: '16%',
        height: '16%',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--amassa-dot, transparent)',
        pointerEvents: 'none',
      }}
    />
  );
}
